import api from "./api";
import { CreateEventPayload, CreateEventResponse, GetEventByIdResponse, UpdateEventPayload, UpdateEventResponse } from "./EventService.types";

/**
 * EventService - Service for unified event creation and management
 *
 * Handles the creation and retrieval of Events through the unified endpoint
 * that combines both Insight and Alert operations.
 *
 * @see REFACTOR_PLAN_UNIFIED_EVENT_ENDPOINT.md for detailed documentation
 */
export default class EventService {
    private readonly basePath = "/v1/event";

    /**
     * Creates a new Event (unified Insight + Alert)
     *
     * This method sends a single request to create both Insight and Alert
     * based on the control flags in the payload.
     *
     * @param payload - The unified event creation payload
     * @returns Promise with the creation response containing IDs of created resources
     * @throws Error if the request fails
     *
     * @example
     * const response = await eventService.createEvent({
     *   shouldCreateInsight: true,
     *   shouldCreateAlert: true,
     *   name: "Storm Warning",
     *   conditionGroups: [...],
     *   locationId: 123,
     *   insight: { dashboardId: 1, restrictionType: true, priorityId: 1 },
     *   alert: { recipients: [...], notifications: [...], channels: [...] }
     * });
     *
     * if (response.success) {
     *   console.log("Insight ID:", response.insightId);
     *   console.log("Alert ID:", response.alertId);
     * }
     */
    async createEvent(payload: CreateEventPayload): Promise<CreateEventResponse> {
        const result = await api.post<CreateEventResponse>(
            this.basePath,
            payload
        );

        if (result.status === 200 || result.status === 201) {
            return result.data;
        }

        throw new Error("Error while creating event");
    }

    /**
     * Gets an Event by its ID (insight ID)
     *
     * This method retrieves the full event details including the insight
     * and any associated alert data.
     *
     * @param eventId - The ID of the event (insight) to retrieve
     * @returns Promise with the event details including insight and optional alert
     * @throws Error if the request fails
     *
     * @example
     * const event = await eventService.getEventById(123);
     * console.log("Insight:", event.insight);
     * console.log("Alert:", event.alert); // May be undefined if no alert associated
     */
    async getEventById(eventId: number): Promise<GetEventByIdResponse> {
        const result = await api.get<GetEventByIdResponse>(
            `${this.basePath}/${eventId}`
        );

        if (result.status === 200) {
            return result.data;
        }

        throw new Error("Error while fetching event");
    }

    /**
     * Updates an Event by its ID
     *
     * This method sends a PUT request to update the event.
     * All fields in the payload are optional - only send what needs to be updated.
     *
     * @param eventId - The ID of the event (insight) to update
     * @param payload - The update payload with optional fields
     * @returns Promise with the update response
     * @throws Error if the request fails
     *
     * @example
     * // Update only the name
     * await eventService.updateEvent(123, { name: "New name" });
     *
     * // Add alert to existing insight
     * await eventService.updateEvent(123, {
     *   alert: {
     *     recipients: [{ userId: 1 }],
     *     notifications: [...],
     *     channels: ["email"]
     *   }
     * });
     *
     * // Delete alert from event
     * await eventService.updateEvent(123, { alert: null });
     */
    async updateEvent(eventId: number, payload: UpdateEventPayload): Promise<UpdateEventResponse> {
        const result = await api.put<UpdateEventResponse>(
            `${this.basePath}/${eventId}`,
            payload
        );

        if (result.status === 200) {
            return result.data;
        }

        throw new Error("Error while updating event");
    }
}
